import type { Language } from '@eformulieren/engine'
import type { Source, SourceType } from '@eformulieren/model'
import { type KeyboardEvent, type ReactNode, useId, useMemo, useRef, useState } from 'react'
import { inLanguage } from '../language/text.js'
import { Dialog } from '../shell/Dialog.js'

/**
 * Een bron kiezen of aanmaken bij het invoegen van een citatie (R-50).
 *
 * Een modale dialoog "Bronvermelding toevoegen" (`shell/Dialog.tsx`, opdracht van 1 november 2026).
 * Tot dan was het een vast paneel onder het veld, en wie in de tekst werkte zag niet dat het was
 * opengegaan of wat er na het sluiten was gebeurd. De dialoog zweeft niet bij de cursor, dus werkt op
 * een tablet net zo (R-158, T14), en op een telefoon is hij het hele scherm. De focus staat bij het
 * openen in het eerste veld: het zoekveld als er bronnen zijn, anders de titel van een nieuwe bron.
 * Escape en Annuleren sluiten hem; daarna staat de cursor weer in de tekst (`useSourcePicker`
 * geeft de keuze pas door als de dialoog dicht is). Sinds de opdracht van 30 oktober 2026 opent hij
 * ook met `@` in de tekst, zoals de lijst van CitatenPOC (`CitationList.tsx`): met de focus in het
 * zoekveld, de bronnen die al in de tekst staan eerst, en Enter kiest de eerste bron die past. Het is ook de plek om de bronnen te
 * beheren, zoals het paneel "Bronnen (n)" van de POC: per bron de auteur en het jaar, wijzigen, en
 * verwijderen zolang de bron nergens in de tekst staat.
 *
 * **Eén bevestiging, twee bronnen voor bron-id's.** Meerdere bestaande bronnen aanvinken kan
 * (een citatie citeert `bronnen: string[]`, meerdere bronnen in één keer is dus al een bestaande
 * mogelijkheid van het model, niet een nieuwe), en een nieuwe bron invullen telt mee zodra de
 * titel niet leeg is. Beide samen is toegestaan: wie een bestaande bron aanvinkt én een nieuwe
 * invult, krijgt één citatie die naar allebei wijst.
 *
 * **Het nieuwe-bronformulier is met opzet minimaal**, naar de opdracht: titel, type, één auteur
 * (achternaam plus voornaam) en jaar. CSL-JSON staat meer toe (`Bron` in het model, meerdere
 * auteurs, container-titel, uitgever, pagina's, URL, DOI); dit paneel schrijft daar niet naartoe.
 * Wie dat nodig heeft vult het later aan via een andere route; er is geen eis die dit paneel
 * dwingt de hele CSL-JSON-vorm aan te bieden.
 */

export const HEADER = { nl: 'Bronvermelding toevoegen', en: 'Add a citation' }
export const NO_SOURCES = { nl: 'Nog geen bronnen.', en: 'No sources yet.' }
const NEW_SOURCE = { nl: 'Nieuwe bron', en: 'New source' }
export const TITLE = { nl: 'Titel', en: 'Title' }
export const TYPE = { nl: 'Type', en: 'Type' }
export const LAST_NAME = { nl: 'Achternaam', en: 'Family name' }
export const FIRST_NAME = { nl: 'Voornaam', en: 'Given name' }
export const YEAR = { nl: 'Jaar', en: 'Year' }
const INSERT = { nl: 'Invoegen', en: 'Insert' }
const INSERTING = { nl: 'Bezig met invoegen…', en: 'Inserting…' }
const CANCEL = { nl: 'Annuleren', en: 'Cancel' }
export const SEARCH = { nl: 'Zoek een bron', en: 'Find a source' }
export const NOT_FOUND = { nl: 'Geen bron gevonden.', en: 'No source found.' }
const IN_TEXT = { nl: 'in de tekst', en: 'in the text' }
export const EDIT = { nl: 'Wijzigen', en: 'Edit' }
export const REMOVE = { nl: 'Verwijderen', en: 'Remove' }
export const EDIT_SOURCE = { nl: 'Bron wijzigen', en: 'Edit source' }
export const SAVE_EDIT = { nl: 'Wijziging opslaan', en: 'Save change' }
export const STOP_EDIT = { nl: 'Niet wijzigen', en: 'Do not change' }
const COUNT = (total: number, used: number): { nl: string; en: string } => ({
  nl: `${String(total)} ${total === 1 ? 'bron' : 'bronnen'}, ${String(used)} in de tekst`,
  en: `${String(total)} ${total === 1 ? 'source' : 'sources'}, ${String(used)} in the text`,
})

export const TYPE_LABELS: Record<SourceType, { nl: string; en: string }> = {
  'article-journal': { nl: 'Artikel', en: 'Journal article' },
  book: { nl: 'Boek', en: 'Book' },
  chapter: { nl: 'Hoofdstuk', en: 'Chapter' },
  report: { nl: 'Rapport', en: 'Report' },
  webpage: { nl: 'Webpagina', en: 'Webpage' },
}
export const SOURCE_TYPES: readonly SourceType[] = ['article-journal', 'book', 'chapter', 'report', 'webpage']

/** Een bron zonder `id`: het paneel maakt hem niet aan, dat doet de aanroeper (naar `nieuwDraadId`). */
export type NewSource = Omit<Source, 'id'>

export interface SourcePanelProps {
  readonly sources: readonly Source[]
  readonly taal: Language
  /** Bevestigen: de gekozen bestaande bron-id's, plus de nieuwe bron als het formulier is ingevuld. */
  readonly onKies: (sources: readonly string[], newSource: NewSource | undefined) => void
  readonly onCancel: () => void
  /** De bronnen die ergens in de tekst van de laag staan: die komen eerst en zijn niet te verwijderen. */
  readonly inUse?: ReadonlySet<string>
  /** Een bron wijzigen, met de nieuwe beschrijving; zonder deze functie is er geen knop. */
  readonly onWijzig?: (sourceId: string, source: NewSource) => void
  /** Een bron die nergens in de tekst staat verwijderen; zonder deze functie is er geen knop. */
  readonly onVerwijder?: (sourceId: string) => void
  /** Een zin over de laatste wijziging, bijvoorbeeld dat een kopie buiten bereik bleef. */
  readonly melding?: string
  /** Na het sluiten van de dialoog, in plaats van de focus terug naar de knop (zie `Dialog`). */
  readonly returnFocus?: () => void
}

/** "Jansen, 2023", "Bakker e.a., 2019", of wat er van auteur en jaar is. */
export function authorYear(source: Source): string {
  const authors = source.author ?? []
  const first = authors[0]
  const name = first === undefined ? undefined : (first.family ?? first.literal ?? first.given)
  const who = name === undefined ? undefined : authors.length > 1 ? `${name} e.a.` : name
  const year = source.issued?.['date-parts']?.[0]?.[0]
  return [who, year === undefined ? undefined : String(year)].filter((part) => part !== undefined).join(', ')
}

/** Kleine letters zonder accenten, zodat "zeegrás" ook "Zeegras" vindt. */
function folded(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
}

/** Of een bron past bij de zoekvraag: elk woord in titel, auteurs of jaar, zonder hoofdletters en accenten. */
export function matches(source: Source, query: string): boolean {
  const needle = folded(query.trim())
  if (needle === '') return true
  const names = (source.author ?? []).flatMap((name) => [name.family, name.given, name.literal])
  const year = source.issued?.['date-parts']?.[0]?.[0]
  const haystack = folded([source.title, ...names, year === undefined ? undefined : String(year)].filter((part) => part !== undefined).join(' '))
  return needle.split(/\s+/).every((word) => haystack.includes(word))
}

/** Wat het formulier van een bron invult: titel, type, de eerste auteur en het jaar. */
export interface SourceFields {
  readonly title: string
  readonly type: SourceType
  readonly lastName: string
  readonly firstName: string
  readonly year: string
}

/**
 * De beschrijving uit het formulier, op een bestaande bron als basis (R-50).
 *
 * Houdt de velden die het formulier niet toont (een tweede auteur, de container-titel) en vervangt
 * alleen titel, type, eerste auteur en jaar. Zonder titel geen bron.
 */
export function describeSource(base: Source | undefined, fields: SourceFields): NewSource | undefined {
  const title = fields.title.trim()
  if (title === '') return undefined
  const rest = base?.author?.slice(1) ?? []
  const lastName = fields.lastName.trim()
  const firstName = fields.firstName.trim()
  const firstAuthor = lastName === '' ? [] : [{ family: lastName, ...(firstName === '' ? {} : { given: firstName }) }]
  const authors = [...firstAuthor, ...rest]
  const { id: _id, author: _author, issued: _issued, ...kept } = base ?? { id: '' }
  const year = fields.year.trim()
  return {
    ...kept,
    type: fields.type,
    title,
    ...(authors.length === 0 ? {} : { author: authors }),
    ...(year === '' || Number.isNaN(Number(year)) ? {} : { issued: { 'date-parts': [[Number(year)]] } }),
  }
}

export function SourcePanel({
  sources,
  taal: language,
  onKies: onChoose,
  onCancel,
  inUse = new Set(),
  onWijzig: onEdit,
  onVerwijder: onRemove,
  melding: notice,
  returnFocus,
}: SourcePanelProps): ReactNode {
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set())
  const [query, setQuery] = useState('')
  const [editing, setEditing] = useState<Source | undefined>(undefined)
  const [title, setTitle] = useState('')
  const [type, setType] = useState<SourceType>('article-journal')
  const [lastName, setLastName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [year, setYear] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const titleRef = useRef<HTMLInputElement>(null)
  const formId = useId()

  // Eerst wat al in de tekst staat, dan op titel: zo staat de bron die u net citeerde bovenaan, en
  // de rest in een volgorde die een mens kan voorspellen (de id's zijn willekeurig).
  const ordered = useMemo(
    () =>
      [...sources].sort((a, b) => {
        const used = Number(inUse.has(b.id)) - Number(inUse.has(a.id))
        return used !== 0 ? used : a.title.localeCompare(b.title, language)
      }),
    [sources, inUse, language],
  )
  const shown = ordered.filter((source) => matches(source, query))
  const usedCount = sources.filter((source) => inUse.has(source.id)).length

  // De focus bij openen (`data-autofocus`, zie `Dialog`): het zoekveld, zodat wie met het toetsenbord
  // werkt meteen kan zoeken; met `@` is dat de afspraak uit de POC. Zonder bronnen het titelveld van een
  // nieuwe bron.

  const newSourceTitle = title.trim()
  const canInsert = chosen.size > 0 || (editing === undefined && newSourceTitle !== '')

  function described(base: Source | undefined): NewSource | undefined {
    return describeSource(base, { title, type, lastName, firstName, year })
  }

  function clearForm(): void {
    setTitle('')
    setType('article-journal')
    setLastName('')
    setFirstName('')
    setYear('')
  }

  function startEdit(source: Source): void {
    setEditing(source)
    setTitle(source.title)
    setType(source.type)
    setLastName(source.author?.[0]?.family ?? '')
    setFirstName(source.author?.[0]?.given ?? '')
    const issued = source.issued?.['date-parts']?.[0]?.[0]
    setYear(issued === undefined ? '' : String(issued))
    requestAnimationFrame(() => titleRef.current?.focus())
  }

  function stopEdit(): void {
    setEditing(undefined)
    clearForm()
  }

  function confirm(): void {
    if (!canInsert) return
    onChoose([...chosen], editing === undefined ? described(undefined) : undefined)
  }

  /** Escape bij het wijzigen van een bron is een stap terug, niet het sluiten van de dialoog. */
  function onKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (event.key !== 'Escape' || editing === undefined) return
    event.preventDefault()
    stopEdit()
  }

  return (
    <Dialog
      taal={language}
      titel={inLanguage(HEADER, language)}
      className="ef-bronnen-kiezer"
      onSluit={onCancel}
      secundair={{ label: inLanguage(CANCEL, language) }}
      primair={{ label: inLanguage(INSERT, language), bezigTekst: inLanguage(INSERTING, language), onActie: confirm, uitgeschakeld: !canInsert }}
      {...(returnFocus === undefined ? {} : { focusTerug: returnFocus })}
    >
      <div className="ef-bronnen-kiezer__inhoud" onKeyDown={onKeyDown}>
      {sources.length === 0 ? (
        <p className="ef-bronnen-kiezer__leeg">{inLanguage(NO_SOURCES, language)}</p>
      ) : (
        <>
          <p className="ef-bronnen-kiezer__telling">{inLanguage(COUNT(sources.length, usedCount), language)}</p>
          <label className="ef-bronnen-kiezer__zoek">
            <span>{inLanguage(SEARCH, language)}</span>
            <input
              ref={searchRef}
              data-autofocus
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                // Enter kiest de eerste bron die past, zoals de lijst achter `@` in de POC; met al een
                // vinkje gezet is Enter gewoon invoegen.
                if (event.key !== 'Enter') return
                event.preventDefault()
                if (chosen.size > 0) confirm()
                else if (shown[0] !== undefined) onChoose([shown[0].id], undefined)
              }}
            />
          </label>
          {shown.length === 0 ? (
            <p className="ef-bronnen-kiezer__leeg">{inLanguage(NOT_FOUND, language)}</p>
          ) : (
            <ul className="ef-bronnen-kiezer__lijst">
              {shown.map((source) => {
                const inputId = `ef-bron-${source.id}`
                const meta = authorYear(source)
                const used = inUse.has(source.id)
                return (
                  <li key={source.id} className="ef-bronnen-kiezer__regel">
                    <input
                      id={inputId}
                      type="checkbox"
                      checked={chosen.has(source.id)}
                      onChange={(event) => {
                        setChosen((current) => {
                          const next = new Set(current)
                          if (event.target.checked) next.add(source.id)
                          else next.delete(source.id)
                          return next
                        })
                      }}
                    />
                    <label htmlFor={inputId} className="ef-bronnen-kiezer__bron">
                      <span className="ef-bronnen-kiezer__titel">{source.title}</span>
                      {meta === '' ? null : <span className="ef-bronnen-kiezer__meta">{meta}</span>}
                      {used ? <span className="ef-bronnen-kiezer__gebruikt">{inLanguage(IN_TEXT, language)}</span> : null}
                    </label>
                    <span className="ef-bronnen-kiezer__beheer">
                      {onEdit === undefined ? null : (
                        <button type="button" className="ef-bronnen-kiezer__knop" aria-label={`${inLanguage(EDIT, language)}: ${source.title}`} onClick={() => startEdit(source)}>
                          {inLanguage(EDIT, language)}
                        </button>
                      )}
                      {onRemove === undefined || used ? null : (
                        <button type="button" className="ef-bronnen-kiezer__knop" data-soort="gevaar" aria-label={`${inLanguage(REMOVE, language)}: ${source.title}`} onClick={() => onRemove(source.id)}>
                          {inLanguage(REMOVE, language)}
                        </button>
                      )}
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </>
      )}
      {notice === undefined ? null : (
        <p className="ef-bronnen-kiezer__melding" role="status">
          {notice}
        </p>
      )}

      <div className="ef-bronnen-kiezer__nieuw" role="group" aria-labelledby={`${formId}-kop`}>
        <h3 className="ef-bronnen-kiezer__subkop" id={`${formId}-kop`}>
          {inLanguage(editing === undefined ? NEW_SOURCE : EDIT_SOURCE, language)}
        </h3>
        <label className="ef-bronnen-kiezer__veld">
          <span>{inLanguage(TITLE, language)}</span>
          <input ref={titleRef} {...(sources.length === 0 ? { 'data-autofocus': true } : {})} type="text" value={title} onChange={(event) => setTitle(event.target.value)} />
        </label>
        <label className="ef-bronnen-kiezer__veld">
          <span>{inLanguage(TYPE, language)}</span>
          <select value={type} onChange={(event) => setType(event.target.value as SourceType)}>
            {SOURCE_TYPES.map((candidate) => (
              <option key={candidate} value={candidate}>
                {inLanguage(TYPE_LABELS[candidate], language)}
              </option>
            ))}
          </select>
        </label>
        <label className="ef-bronnen-kiezer__veld">
          <span>{inLanguage(LAST_NAME, language)}</span>
          <input type="text" value={lastName} onChange={(event) => setLastName(event.target.value)} />
        </label>
        <label className="ef-bronnen-kiezer__veld">
          <span>{inLanguage(FIRST_NAME, language)}</span>
          <input type="text" value={firstName} onChange={(event) => setFirstName(event.target.value)} />
        </label>
        <label className="ef-bronnen-kiezer__veld">
          <span>{inLanguage(YEAR, language)}</span>
          <input type="number" inputMode="numeric" value={year} onChange={(event) => setYear(event.target.value)} />
        </label>
        {editing === undefined ? null : (
          <div className="ef-bronnen-kiezer__acties">
            <button
              type="button"
              className="ef-bronnen-kiezer__invoegen"
              disabled={newSourceTitle === ''}
              onClick={() => {
                const changed = described(editing)
                if (changed === undefined) return
                onEdit?.(editing.id, changed)
                stopEdit()
              }}
            >
              {inLanguage(SAVE_EDIT, language)}
            </button>
            <button type="button" className="ef-bronnen-kiezer__annuleer" onClick={stopEdit}>
              {inLanguage(STOP_EDIT, language)}
            </button>
          </div>
        )}
      </div>

      </div>
    </Dialog>
  )
}
